import React, { useMemo, useRef, useState, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { Popover, PopoverHeader } from 'reactstrap';
import styled from 'styled-components';

// Components
import { useSendMessageModal } from '@molecules/SendMessageModal';
import MailSVG from './MailSVG';
import { ButtonNoStyles } from '@styled';

// Functionality
import useClickOutside from '@hooks/useClickOutside';
import useIsMemberActiveMember from '@hooks/useIsMemberActive';
import { useScreenMinMd } from '@hooks/useResponsive';
import { getLoginPath } from '../../../../icsc-app/js/helpers/url.helpers';

const Container = styled.div`
    display: inline-block;
    margin-right: 1.5rem;

    .tooltip {
        filter: drop-shadow(0px 0px 16px rgba(87, 87, 87, 0.2));
        padding: 8px 16px;
        white-space: nowrap;

        &:after {
            border-left: 10px solid transparent !important;
            border-right: 10px solid transparent !important;
            border-top-width: 10px !important;
            bottom: -10px !important;
        }
    }
`;

const randomIntFromInterval = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

const getPopoverPlacement = ({ isDesktop, tooltipPlacement, tooltipPlacementMobile }) => {
    let placement;
    // from props
    if (isDesktop && tooltipPlacement) {
        placement = tooltipPlacement;
    }
    if (!isDesktop && tooltipPlacementMobile) {
        placement = tooltipPlacementMobile;
    }
    return placement;
};

const getClickOutsideException = target => $('.popover')[0] && !$.contains($('.popover')[0], $(target)[0]);

const MailIcon = props => {
    const {
        id,
        graphID,
        isLoggedIn = false,
        tooltipPlacement = 'bottom',
        tooltipPlacementMobile = 'left',
        isLoading = false,
        onMessage = () => {},
        companyID,
        companyName,
        displayLocation,
        fullName,
        firstName,
        lastName,
        designations,
        businessType,
        title,
        isFavorite,
        photoURL,
        status,
        showMemberDetails = true,
        showMemberDetailsCTA = true,
    } = props;

    const [showIcon, setShowIcon] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const isDesktop = useScreenMinMd();
    const clickOutsideRef = useRef(null);
    const isMemberActive = useIsMemberActiveMember({ status });
    const { openModal: openSendMessageModal } = useSendMessageModal();

    useEffect(() => {
        if (!status || !isMemberActive) {
            setShowIcon(false);
        } else {
            setShowIcon(true);
        }
    }, [isMemberActive, status]);

    useClickOutside({
        ref: clickOutsideRef,
        onClickOutside: () => setPopoverOpen(false),
        exceptionCb: getClickOutsideException,
    });

    const popoverId = useMemo(() => `tooltip-${randomIntFromInterval(1, 1000)}`, []);

    const placement = useMemo(
        () =>
            getPopoverPlacement({
                isDesktop,
                tooltipPlacement,
                tooltipPlacementMobile,
            }),
        [isDesktop, tooltipPlacement, tooltipPlacementMobile]
    );

    const handleKeyDown = e => {
        if (e.keyCode === 13) {
            handleOnClick();
        }
    };

    const handleOnClick = () => {
        if (isLoading) {
            return;
        }

        openSendMessageModal({
            businessType,
            companyID,
            companyName,
            designations,
            displayLocation,
            firstName,
            fullName,
            graphID,
            id,
            lastName,
            photoURL,
            title,
            isFavorite,
            onMessage,
            showMemberDetails,
            showMemberDetailsCTA,
        });
    };

    const toggle = () => {
        setPopoverOpen(!popoverOpen);
    };

    if (!showIcon) {
        return null;
    }

    return (
        <Container>
            {isLoggedIn && (
                <>
                    <ButtonNoStyles
                        ref={clickOutsideRef}
                        id={popoverId}
                        data-tip
                        data-for={popoverId}
                        type="button"
                        tabIndex={0}
                        onClick={handleOnClick}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                        style={{ lineHeight: '0' }}
                    >
                        <MailSVG />
                        <span className="sr-only">Send Message</span>
                    </ButtonNoStyles>

                    <ReactTooltip id={popoverId} className="tooltip" type="light" effect="solid">
                        <span>Send Message</span>
                    </ReactTooltip>
                </>
            )}

            {!isLoggedIn && (
                <>
                    <div>
                        <Popover
                            trigger="click"
                            placement={placement}
                            target={popoverId}
                            isOpen={popoverOpen}
                            toggle={toggle}
                            popperClassName="popover-react"
                        >
                            <PopoverHeader>
                                <div className="bg-gray-dark p-2">
                                    <a href={getLoginPath()} target="_self" className="light">
                                        Log In to Message
                                    </a>
                                </div>
                            </PopoverHeader>
                        </Popover>

                        <ButtonNoStyles ref={clickOutsideRef} id={popoverId} type="button" style={{ lineHeight: '0' }}>
                            <MailSVG />
                            <span className="sr-only">Log In to Save</span>
                        </ButtonNoStyles>
                    </div>
                </>
            )}
        </Container>
    );
};

MailIcon.propTypes = {
    id: PropTypes.number,
    graphID: PropTypes.string,
    isLoggedIn: PropTypes.bool,
    tooltipPlacement: PropTypes.string,
    tooltipPlacementMobile: PropTypes.string,
    companyID: PropTypes.number,
    companyName: PropTypes.string,
    title: PropTypes.string,
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    designations: PropTypes.string,
    displayLocation: PropTypes.string,
    businessType: PropTypes.array,
    isFavorite: PropTypes.bool,
    isLoading: PropTypes.bool,
    status: PropTypes.string,
    photoURL: PropTypes.string,
    onMessage: PropTypes.func,
    showMemberDetails: PropTypes.bool,
    showMemberDetailsCTA: PropTypes.bool,
};

export default MailIcon;
